import type { JSX } from 'react';
import { useEffect, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileTextOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { openingApi, type LeaseContractFields } from '../lib/wikiApi';

/**
 * Блок A — генератор договора аренды (.docx). Единый шаблон: Арендатор = наш
 * партнёр (реквизиты подтягиваются автоматически из карточки), Арендодатель и
 * условия — вводит партнёр. Общий компонент для двух мест:
 *  1) карточка одобренной заявки осмотра (RoomReadinessPage);
 *  2) статья базы знаний «Подписать договор аренды» (ArticlePage по slug).
 */

const inputStyle: CSSProperties = {
  width: '100%', border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)',
  padding: '8px 11px', fontFamily: 'inherit', fontSize: 13.5, outline: 'none',
};
const labelStyle: CSSProperties = { fontSize: 11.5, fontWeight: 700, color: 'var(--fk-text-2)', marginBottom: 5 };
const sectionTitle: CSSProperties = {
  fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.4px',
  color: 'var(--fk-text-muted)', margin: '18px 0 8px',
};

/** Форма договора для одной одобренной заявки осмотра (id). */
export function LeaseContractForm({ id }: { id: string }): JSX.Element {
  const [fields, setFields] = useState<LeaseContractFields>({ мощность_квт: '20' });
  const [busy, setBusy] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [genErr, setGenErr] = useState<string | null>(null);

  const set = (k: keyof LeaseContractFields, v: string): void =>
    setFields((prev) => ({ ...prev, [k]: v }));

  // Подтягиваем авто-реквизиты арендатора (наш партнёр) + адрес/площадь.
  const prefill = useQuery({
    queryKey: ['wiki', 'lease-prefill', id],
    queryFn: () => openingApi.leaseContractPrefill(id),
    retry: false,
  });
  useEffect(() => {
    if (prefill.data && !prefilled) {
      setFields((prev) => ({ ...prev, ...prefill.data }));
      setPrefilled(true);
    }
  }, [prefill.data, prefilled]);

  const download = async (): Promise<void> => {
    setBusy(true);
    setGenErr(null);
    try {
      const blob = await openingApi.generateLeaseContract(id, fields);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const city = (fields.город ?? '').trim();
      a.download = `Договор аренды${city ? ' — ' + city : ''}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setGenErr(e instanceof Error ? e.message : 'Не удалось сформировать договор');
    } finally {
      setBusy(false);
    }
  };

  const field = (label: string, k: keyof LeaseContractFields, placeholder?: string): JSX.Element => (
    <div>
      <div style={labelStyle}>{label}</div>
      <input value={fields[k] ?? ''} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} style={inputStyle} />
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)', marginBottom: 4 }}>
        Реквизиты арендатора (вы) подтянуты из вашей карточки — при необходимости
        поправьте. Данные арендодателя и условия аренды заполните ниже. Скачанный
        .docx можно доработать в Word.
      </div>

      {prefill.isLoading && <div style={{ fontSize: 13, color: 'var(--fk-text-muted)', marginTop: 8 }}>Загружаем данные…</div>}

      {/* ── Договор: номер/дата ── */}
      <div style={sectionTitle}>Договор</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {field('Номер договора', 'номер_договора', 'напр. АР-2026-01')}
        {field('Дата', 'дата', 'ДД.ММ.ГГГГ')}
      </div>

      {/* ── Арендодатель (вводит партнёр) ── */}
      <div style={sectionTitle}>Арендодатель</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {field('Наименование / ФИО', 'арендодатель_наименование', 'ИП / ООО / физлицо')}
        {field('Основание собственности', 'основание_собственности', 'свидетельство / договор')}
      </div>

      {/* ── Объект и условия ── */}
      <div style={sectionTitle}>Объект и условия</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {field('Город', 'город')}
        {field('Адрес помещения', 'адрес_помещения')}
        {field('Площадь, м²', 'площадь_м2')}
        {field('Мощность, кВт', 'мощность_квт', '20')}
        {field('Срок аренды, лет', 'срок_аренды_лет', 'напр. 3')}
        {field('Льготных месяцев', 'льготных_месяцев', 'напр. 1')}
        {field('Ставка льготная, ₽/мес', 'ставка_льготная', 'напр. 50 000')}
        {field('Ставка основная, ₽/мес', 'ставка_основная', 'напр. 75 000')}
        {field('Обеспечительный платёж, ₽', 'обеспечительный_платёж', 'напр. 75 000')}
      </div>

      {/* ── Арендатор (вы) — авто, можно поправить ── */}
      <div style={sectionTitle}>Арендатор (вы) — заполнено автоматически</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {field('Наименование', 'арендатор_наименование')}
        {field('Директор / ИП', 'арендатор_директор')}
        {field('ИНН', 'арендатор_инн')}
        {field('КПП', 'арендатор_кпп')}
        {field('ОГРН / ОГРНИП', 'арендатор_огрн')}
        {field('Юр. адрес', 'арендатор_юр_адрес')}
        {field('Телефон', 'арендатор_телефон')}
        {field('E-mail', 'арендатор_email')}
        {field('Расчётный счёт', 'арендатор_рс')}
        {field('Банк', 'арендатор_банк')}
        {field('Корр. счёт', 'арендатор_кс')}
        {field('БИК', 'арендатор_бик')}
        {field('Договор франшизы №', 'договор_франшизы_номер')}
      </div>

      {genErr && (
        <div style={{ fontSize: 13, color: 'var(--fk-error)', marginTop: 12, fontWeight: 600 }}>{genErr}</div>
      )}

      <button
        type="button"
        onClick={() => void download()}
        disabled={busy}
        style={{
          marginTop: 18, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px',
          borderRadius: 'var(--fk-radius-md)', border: 'none',
          background: busy ? 'var(--fk-border-strong)' : 'var(--fk-info, #0096FF)',
          color: '#fff', fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit',
        }}>
        <FileTextOutlined /> {busy ? 'Формируем…' : 'Скачать .docx'}
      </button>
    </div>
  );
}

/** Признак одобренной заявки осмотра (статус/стадия). */
function isApproved(it: { status: string; stageCode: string | null }): boolean {
  return it.status === 'APPROVED' || it.status === 'CONVERTED' || it.stageCode === 'approved';
}

/**
 * Обёртка для статьи БЗ: показывает одобренные заявки партнёра и даёт выбрать
 * помещение, по которому формировать договор. Если одобренных нет — подсказка
 * со ссылкой на анализ помещения.
 */
export function LeaseContractPicker(): JSX.Element {
  const q = useQuery({
    queryKey: ['wiki', 'my-room-inspections'],
    queryFn: () => openingApi.myRoomInspections(),
    staleTime: 30_000,
    retry: false,
  });
  const approved = (q.data ?? []).filter(isApproved);
  const [picked, setPicked] = useState<string | null>(null);

  // Если одобренная заявка одна — выбираем её автоматически.
  useEffect(() => {
    if (!picked && approved.length === 1) setPicked(approved[0].id);
  }, [approved, picked]);

  const card: CSSProperties = {
    marginTop: 8, border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-card)',
    padding: 18, background: 'var(--fk-surface)',
  };

  if (q.isLoading) {
    return <div style={{ ...card, color: 'var(--fk-text-muted)', fontSize: 14 }}>Загружаем ваши заявки…</div>;
  }

  if (approved.length === 0) {
    return (
      <div style={{ ...card, fontSize: 14, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 800, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileTextOutlined style={{ color: 'var(--fk-info, #0096FF)' }} /> Договор аренды
        </div>
        Договор станет доступен здесь после того, как куратор <b>одобрит</b> вашу заявку на
        помещение. Сначала пройдите «Анализ помещения» и дождитесь одобрения.
        <div style={{ marginTop: 12 }}>
          <Link
            to="/opening/room-check"
            style={{ fontWeight: 700, color: 'var(--fk-info, #0096FF)', textDecoration: 'none' }}>
            Перейти к анализу помещения →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ fontWeight: 800, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }}>
        <FileTextOutlined style={{ color: 'var(--fk-info, #0096FF)' }} /> Договор аренды
      </div>

      {/* Выбор помещения, если одобренных заявок несколько */}
      {approved.length > 1 && (
        <div style={{ marginBottom: 14 }}>
          <div style={labelStyle}>Помещение</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {approved.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => setPicked(it.id)}
                style={{
                  textAlign: 'left', padding: '9px 12px', borderRadius: 'var(--fk-radius-md)', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13.5,
                  border: picked === it.id ? '1.5px solid var(--fk-info, #0096FF)' : '1px solid var(--fk-border)',
                  background: picked === it.id ? 'rgba(0,150,255,.08)' : 'var(--fk-surface)',
                  color: 'var(--fk-text)',
                }}>
                {it.address || 'Без адреса'}
              </button>
            ))}
          </div>
        </div>
      )}

      {picked && <LeaseContractForm id={picked} />}
    </div>
  );
}
